import type { SavedPath } from "../../core/document/Document";
import type { PathModel } from "../../core/document/PathModel";
import type { EditorState } from "../state/EditorState";
import { SetWorkPathCommand, RemoveWorkPathCommand, RenameWorkPathCommand } from "../../core/commands/SetWorkPathCommand";

// i31 — the Paths panel used to keep its own local `paths` array, which
// nothing ever populated (the only writer, addPath(), was wired to a
// `.make-path-btn` that doesn't exist in index.html) and which never
// reflected `Document.workPaths` — the real, undoable path store that
// PenTool's "path" output mode (main.ts's PenTool -> SetWorkPathCommand)
// and the Edit > "Make Work Path…" flow both write to.
//
// The panel now renders straight from `state.doc.workPaths` and re-renders
// reactively via state.subscribe(), and every mutation (add/delete/rename)
// goes through a Command via state.execute(), per the app-wide convention.
export class PathsPanel {
  private container: HTMLElement | null = null;
  private listEl: HTMLElement | null = null;
  private state: EditorState | null = null;
  private unsub: (() => void) | null = null;

  init(state: EditorState): void {
    this.state = state;
    // The real DOM (index.html) has `.panel__body.paths-body`, not
    // `.paths-panel` — that selector mismatch was why init() always
    // returned early and the panel never set up listEl/render/bindEvents.
    this.container = document.querySelector(".paths-body") ?? null;
    if (!this.container) return;

    this.listEl = this.container.querySelector(".paths-list") ?? null;
    if (!this.listEl) {
      this.listEl = document.createElement("div");
      this.listEl.className = "paths-list";
      this.container.appendChild(this.listEl);
    }

    this.render();
    this.bindEvents();
    if (!this.unsub) {
      this.unsub = state.subscribe(() => this.render());
    }
  }

  destroy(): void {
    if (this.unsub) this.unsub();
    this.unsub = null;
  }

  private get workPaths(): readonly SavedPath[] {
    return this.state?.doc?.workPaths ?? [];
  }

  addPath(path: PathModel, name?: string): void {
    if (!this.state) return;
    const label = name ?? `Path ${this.workPaths.length + 1}`;
    this.state.execute(new SetWorkPathCommand(path, label));
  }

  deletePath(id: string): void {
    if (!this.state) return;
    this.state.execute(new RemoveWorkPathCommand(id));
  }

  renamePath(id: string, newName: string): void {
    if (!this.state || !newName.trim()) return;
    this.state.execute(new RenameWorkPathCommand(id, newName.trim()));
  }

  getPath(id: string): PathModel | null {
    const found = this.workPaths.find(p => p.id === id);
    return found ? JSON.parse(JSON.stringify(found.path)) : null;
  }

  render(): void {
    if (!this.listEl) return;
    this.listEl.innerHTML = "";

    const paths = this.workPaths;
    if (paths.length === 0) {
      const empty = document.createElement("div");
      empty.className = "paths-empty muted";
      empty.textContent = "No work paths";
      this.listEl.appendChild(empty);
      return;
    }

    for (const path of paths) {
      const item = document.createElement("div");
      item.className = "path-item";
      item.dataset.pathId = path.id;

      const nameEl = document.createElement("span");
      nameEl.className = "path-name";
      nameEl.textContent = path.name;
      nameEl.addEventListener("dblclick", () => {
        const newName = prompt("Rename path:", path.name);
        if (newName && newName.trim()) {
          this.renamePath(path.id, newName);
        }
      });

      const deleteBtn = document.createElement("button");
      deleteBtn.className = "path-delete";
      deleteBtn.textContent = "×";
      deleteBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.deletePath(path.id);
      });

      item.appendChild(nameEl);
      item.appendChild(deleteBtn);
      this.listEl.appendChild(item);
    }
  }

  private bindEvents(): void {
    if (!this.container) return;

    // NOTE (producer gap): index.html has no ".make-path-btn" element in
    // the Paths panel body, so this never fires today. The real producer
    // for workPaths is PenTool's "path" output mode (see SetWorkPathCommand
    // call sites) and main.ts's makeWorkPath() (Edit > Make Work Path…,
    // which currently only stores the result in the module-local
    // `currentPathModel` and doesn't persist it — a separate, pre-existing
    // gap left as-is here since fixing it is outside i31's scope of "make
    // the panel actually list whatever path state exists").
    const makePathBtn = this.container.querySelector(".make-path-btn");
    if (makePathBtn) {
      makePathBtn.addEventListener("click", () => {
        this.makePathFromSelection();
      });
    }
  }

  private makePathFromSelection(): void {
    if (!this.state?.doc?.selection) return;
    const sel = this.state.doc.selection;
    const bounds = sel.bounds();
    if (!bounds) return;

    const path: PathModel = {
      subpaths: [{
        points: [
          { x: bounds.x, y: bounds.y, type: "corner" },
          { x: bounds.x + bounds.w, y: bounds.y, type: "corner" },
          { x: bounds.x + bounds.w, y: bounds.y + bounds.h, type: "corner" },
          { x: bounds.x, y: bounds.y + bounds.h, type: "corner" },
        ],
        closed: true,
      }],
    };

    this.addPath(path, "Work Path");
  }
}
